import { useEffect, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Link2, Loader2, Unlink } from "lucide-react";
import { toast } from "sonner";
import {
  useLinkReturnItem,
  useUnlinkReturnItem,
  useReturnProductSearch,
  type ProductCandidate,
  type ReturnItemLink,
  type ReturnLinkRole,
} from "@/hooks/use-returns";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";

const STOCK_LABEL: Record<string, string> = {
  instock: "na stanju",
  outofstock: "nema na stanju",
  onbackorder: "na upit",
};

function stockLabel(status: string): string {
  return STOCK_LABEL[status] || status || "—";
}

/** Kupčev slobodan tekst iz forme, spojen za prikaz ("model · boja · veličina"). */
function displayText(parts: (string | undefined)[]): string {
  return parts.filter(Boolean).join(" · ");
}

/** Isti tekst, razmakom umjesto tačkom — koristi se kao predpopunjen upit za pretragu. */
function searchDefault(parts: (string | undefined)[]): string {
  return parts.filter(Boolean).join(" ");
}

type ReturnItemRowProps = {
  returnId: string;
  storeId: string;
  role: ReturnLinkRole;
  itemIndex: number;
  /** Kupčev unos za ovu stranu, npr. [it.model, it.color, it.size]. */
  parts: (string | undefined)[];
  link: ReturnItemLink | undefined;
};

/** Prikaz i povezivanje jedne "strane" stavke zahtjeva (vraćeni/zamjenski/dodatni proizvod)
 *  sa stvarnim proizvodom i varijacijom iz kataloga. */
export function ReturnItemRow({ returnId, storeId, role, itemIndex, parts, link }: ReturnItemRowProps) {
  const [open, setOpen] = useState(false);
  const text = displayText(parts);
  const defaultQuery = searchDefault(parts);
  const [queryInput, setQueryInput] = useState(defaultQuery);
  const [debouncedQuery, setDebouncedQuery] = useState(defaultQuery);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const linkMutation = useLinkReturnItem(returnId);
  const unlinkMutation = useUnlinkReturnItem(returnId);
  const search = useReturnProductSearch(storeId, open ? debouncedQuery : "");

  function openPanel() {
    setQueryInput(defaultQuery);
    setDebouncedQuery(defaultQuery);
    setOpen(true);
  }

  function closePanel() {
    setOpen(false);
  }

  useEffect(() => {
    if (!open) return;
    if (queryInput === debouncedQuery) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedQuery(queryInput), 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryInput, open]);

  async function handleSelect(candidate: ProductCandidate) {
    try {
      await linkMutation.mutateAsync({
        role,
        itemIndex,
        productId: candidate.productId,
        variationId: candidate.variationId,
        productName: candidate.productName,
        variationName: candidate.variationName,
        sku: candidate.sku,
        isVariable: candidate.isVariable,
      });
      toast.success("Proizvod povezan");
      setOpen(false);
    } catch (e: any) {
      toast.error(e.message || "Greška pri povezivanju proizvoda");
    }
  }

  async function handleUnlink() {
    try {
      await unlinkMutation.mutateAsync({ role, itemIndex });
      toast.success("Veza uklonjena");
    } catch (e: any) {
      toast.error(e.message || "Greška pri uklanjanju veze");
    }
  }

  if (link) {
    return (
      <div className="flex flex-col gap-0.5 text-sm">
        <div className="flex items-center gap-1.5 flex-wrap">
          <Link
            to="/products/$productId"
            params={{ productId: String(link.productId) }}
            className="text-primary hover:underline font-medium"
          >
            {link.productName}
          </Link>
          {link.variationName && <span className="text-muted-foreground text-xs">{link.variationName}</span>}
          {/* Varijabilan proizvod povezan bez varijacije: veza je nepotpuna — analitika varijacija
              je ne broji, pa ne smije izgledati kao uredno povezan jednostavan proizvod. */}
          {link.isVariable && link.variationId === null && (
            <span
              title="Proizvod je varijabilan, ali varijacija nije određena — nedostaje u statistici varijacija"
              className="text-[10px] px-1 py-0.5 border bg-yellow-500/15 text-yellow-400 border-yellow-500/20"
            >
              varijacija nije određena
            </span>
          )}
          {link.sku && <span className="text-muted-foreground text-xs font-mono">{link.sku}</span>}
          {/* Veza bez korisnika dolazi s javne forme — operater treba znati da je izbor kupčev. */}
          {link.linkedBy === null && (
            <span
              title="Proizvod je odabrao kupac na formi za povrat"
              className="text-[10px] px-1 py-0.5 border bg-sky-500/15 text-sky-400 border-sky-500/20"
            >
              kupac odabrao
            </span>
          )}
          <button
            type="button"
            onClick={handleUnlink}
            disabled={unlinkMutation.isPending}
            title="Ukloni vezu"
            className="text-muted-foreground hover:text-red-400 disabled:opacity-50"
          >
            <Unlink className="h-3 w-3" />
          </button>
        </div>
        {text && <div className="text-[11px] text-muted-foreground">{text}</div>}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className="text-sm">{text || <span className="text-muted-foreground">—</span>}</span>
        <Button variant="outline" size="sm" className="h-7 text-xs" onClick={openPanel}>
          <Link2 className="h-3 w-3 mr-1" /> Poveži
        </Button>
      </div>
      {open && (
        <div className="ring-1 ring-foreground/10 bg-background rounded p-2 flex flex-col gap-1.5 max-w-md">
          <Input
            autoFocus
            value={queryInput}
            onChange={(e) => setQueryInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                e.preventDefault();
                closePanel();
              }
            }}
            placeholder="Pretraga proizvoda..."
            className="text-xs h-8"
          />
          <div className="flex flex-col gap-0.5 max-h-64 overflow-y-auto">
            {search.isLoading && (
              <div className="flex flex-col gap-1 py-1">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-8 w-full" />
                ))}
              </div>
            )}
            {!search.isLoading && search.isError && (
              <div className="text-xs text-red-400 px-1 py-2">
                {(search.error as any)?.message || "Greška pri pretrazi proizvoda"}
              </div>
            )}
            {!search.isLoading && !search.isError && debouncedQuery.trim().length < 2 && (
              <div className="text-xs text-muted-foreground px-1 py-2">Unesite najmanje 2 znaka</div>
            )}
            {!search.isLoading && !search.isError && debouncedQuery.trim().length >= 2 && (
              <>
                {(search.data?.candidates?.length ?? 0) === 0 && (
                  <div className="text-xs text-muted-foreground px-1 py-2">
                    Nema rezultata za "{debouncedQuery.trim()}"
                  </div>
                )}
                {search.data?.candidates.map((c) => (
                  <button
                    key={`${c.productId}-${c.variationId ?? "base"}`}
                    type="button"
                    onClick={() => handleSelect(c)}
                    disabled={linkMutation.isPending}
                    className="w-full text-left px-1.5 py-1 hover:bg-muted/70 flex items-center gap-2 text-xs rounded disabled:opacity-50"
                  >
                    {c.image ? (
                      <img src={c.image} alt="" loading="lazy" className="h-8 w-8 object-cover shrink-0 ring-1 ring-foreground/10" />
                    ) : (
                      <div className="h-8 w-8 shrink-0 bg-muted ring-1 ring-foreground/10" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="truncate font-medium">{c.productName}</div>
                      <div className="truncate text-muted-foreground">
                        {c.isVariable && c.variationId === null
                          ? "varijacija nepoznata"
                          : c.variationName || "—"}
                        {c.sku && ` · ${c.sku}`}
                        {" · "}
                        {stockLabel(c.stockStatus)}
                      </div>
                    </div>
                  </button>
                ))}
              </>
            )}
          </div>
          <div className="flex justify-end">
            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={closePanel} disabled={linkMutation.isPending}>
              {linkMutation.isPending && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
              Zatvori
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
